import {
  useCallback,
  useContext,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type FocusEvent as ReactFocusEvent,
  type HTMLAttributes,
  type ReactNode,
  type Ref,
  type RefObject,
} from 'react'
import { createPortal } from 'react-dom'
import { cx, focusablesIn, PortalThemeContext, useControllableState } from '@/shared/lib'
import { Surface } from '@/shared/ui/Surface'
import s from './Popover.module.css'

/** Желаемая сторона и выравнивание. Сторона — пожелание: не влезло — панель перевернётся. */
export type PopoverPlacement = 'bottom-start' | 'bottom-end' | 'top-start' | 'top-end'

/** Сторона, на которой панель оказалась в итоге. Наружу уходит атрибутом `data-side`. */
export type PopoverSide = 'top' | 'bottom'

export type PopoverProps = {
  /** Элемент, к которому привязана панель. Триггер рисует потребитель — это его разметка. */
  anchorRef: RefObject<HTMLElement | null>
  /** Управляемый режим: состоянием владеет тот, кто рисует триггер. */
  open?: boolean
  /** Неуправляемый режим: панель открыта с монтирования и умеет закрыться сама. */
  defaultOpen?: boolean
  /** Просьба закрыться: Esc, нажатие вне, уход фокуса. Открывает панель триггер, не компонент. */
  onOpenChange?: (open: boolean) => void
  placement?: PopoverPlacement
  /** Ширина ровно по якорю: оверлей поиска на всю ширину поля, выпадашка Select. */
  matchAnchorWidth?: boolean
  /** Перенести фокус в панель при открытии: меню и списки опций, где сразу нужны стрелки. */
  autoFocus?: boolean
  children: ReactNode
  /** Указывает на корень панели: измерение, прокрутка к выбранному элементу списка. */
  ref?: Ref<HTMLDivElement>
} & Omit<HTMLAttributes<HTMLDivElement>, 'children'>

/**
 * Зазор до якоря. Кит рисует 6 / 8 / 10 — сведено к одной ступени, значение зеркалит --space-2.
 * Живёт константой, а не токеном: участвует в арифметике координат, а читать
 * getComputedStyle на каждый кадр прокрутки — плохой обмен.
 */
const ANCHOR_GAP = 8

/** Отступ от края экрана при прижатии. Зеркалит --space-3, тот же довод, что у ANCHOR_GAP. */
const VIEWPORT_MARGIN = 12

/** Пол доступной высоты: у якоря на самом краю экрана панель всё равно должна быть читаемой. */
const MIN_AVAILABLE_HEIGHT = 120

type PanelPosition = {
  top: number
  left: number
  /** Сколько места осталось на выбранной стороне: уезжает в --popover-available-h. */
  available: number
  side: PopoverSide
  anchorWidth: number
}

/** Инлайновый стиль панели: координаты и две переменные, которые считаются по месту. */
type PanelStyle = CSSProperties & Record<`--${string}`, string>

const clamp = (value: number, low: number, high: number): number =>
  Math.min(Math.max(value, low), high)

/** Субпиксельный дребезг getBoundingClientRect не должен гонять рендер на каждом кадре. */
const round = (value: number): number => Math.round(value * 100) / 100

/**
 * Координаты панели в системе окна (`position: fixed`).
 * Переворот и прижатие к краям — единственные два правила: больше кит не требует.
 */
function computePosition(
  anchor: HTMLElement,
  panel: HTMLElement,
  placement: PopoverPlacement,
  matchAnchorWidth: boolean,
): PanelPosition {
  const rect = anchor.getBoundingClientRect()
  /*
   * clientWidth/clientHeight, а не innerWidth/innerHeight: полоса прокрутки в видимую область
   * не входит, и панель не должна на неё наезжать. Запасной вариант — для сред, где раскладки
   * нет вовсе и оба значения нулевые (jsdom): иначе экран считался бы размером 0×0.
   */
  const viewportWidth = document.documentElement.clientWidth || window.innerWidth
  const viewportHeight = document.documentElement.clientHeight || window.innerHeight

  /*
   * Высота содержимого, а не отрисованная: панель, однажды прижатая к низу экрана, считала бы
   * себя низкой по offsetHeight и больше никогда не переворачивалась бы обратно.
   */
  const contentHeight = Math.max(panel.scrollHeight, panel.offsetHeight)
  const spaceBelow = viewportHeight - rect.bottom - ANCHOR_GAP - VIEWPORT_MARGIN
  const spaceAbove = rect.top - ANCHOR_GAP - VIEWPORT_MARGIN

  const wanted: PopoverSide = placement.startsWith('top') ? 'top' : 'bottom'
  const wantedSpace = wanted === 'bottom' ? spaceBelow : spaceAbove
  const otherSpace = wanted === 'bottom' ? spaceAbove : spaceBelow
  const other: PopoverSide = wanted === 'bottom' ? 'top' : 'bottom'
  // Переворот только когда содержимое не влезло и на другой стороне места действительно больше
  const side = contentHeight <= wantedSpace || otherSpace <= wantedSpace ? wanted : other

  const available = Math.max(
    side === 'bottom' ? spaceBelow : spaceAbove,
    Math.min(MIN_AVAILABLE_HEIGHT, contentHeight),
  )
  // offsetHeight уже зажат max-height предыдущего прохода — именно он определяет верхний край
  const height = Math.min(panel.offsetHeight, available)
  const width = matchAnchorWidth ? rect.width : panel.offsetWidth

  const alignedLeft = placement.endsWith('end') ? rect.right - width : rect.left
  const alignedTop = side === 'bottom' ? rect.bottom + ANCHOR_GAP : rect.top - ANCHOR_GAP - height

  return {
    left: round(
      clamp(
        alignedLeft,
        VIEWPORT_MARGIN,
        Math.max(VIEWPORT_MARGIN, viewportWidth - width - VIEWPORT_MARGIN),
      ),
    ),
    top: round(
      clamp(
        alignedTop,
        VIEWPORT_MARGIN,
        Math.max(VIEWPORT_MARGIN, viewportHeight - height - VIEWPORT_MARGIN),
      ),
    ),
    available: round(available),
    side,
    anchorWidth: round(rect.width),
  }
}

const samePosition = (a: PanelPosition | null, b: PanelPosition): boolean =>
  a !== null &&
  a.top === b.top &&
  a.left === b.left &&
  a.available === b.available &&
  a.side === b.side &&
  a.anchorWidth === b.anchorWidth

/**
 * Плавающая панель, привязанная к якорю: фильтр колонки в реестре, меню колонок, выбор склада,
 * соседи хлебной крошки, подсказки поиска по каталогу, колокольчик уведомлений. Накрывает
 * контент и не двигает вёрстку. Что внутри — решает потребитель: это транспорт, а не смысл.
 *
 * Позиционируется порталом в body и `position: fixed`, а не приёмом кита (`absolute` от соседней
 * обёртки): панель фильтра колонки живёт в шапке таблицы, которую режет `overflow`, а
 * `backdrop-filter` стеклянных предков ломает `fixed` внутри себя.
 *
 * Ловушки фокуса нет — это не модалка. Но Tab не уводит в фон при открытой панели: с якоря он
 * заходит внутрь, а с краёв панели закрывает её и возвращает фокус на якорь.
 *
 * Контракт: docs/ui-kit/contracts/popover.md
 */
export function Popover({
  anchorRef,
  open,
  defaultOpen = false,
  onOpenChange,
  placement = 'bottom-start',
  matchAnchorWidth = false,
  autoFocus = false,
  children,
  className,
  id,
  onBlur,
  ref,
  ...rest
}: PopoverProps) {
  // Хук — до ранних return: тема экрана, из которого открыт портал (вход — тёмный)
  const portalTheme = useContext(PortalThemeContext)

  const generatedId = useId()
  const panelId = id ?? generatedId

  const [position, setPosition] = useState<PanelPosition | null>(null)
  const panelRef = useRef<HTMLDivElement | null>(null)

  const [isOpen, setOpen] = useControllableState(open, defaultOpen, onOpenChange)

  const close = useCallback(() => setOpen(false), [setOpen])

  /** Панель нужна и снаружи (потребителю), и внутри (измерения): ref раздваивается здесь. */
  const attachPanel = useCallback(
    (node: HTMLElement | null) => {
      // Surface типизирует ref как HTMLElement; сужение проверкой, а не приведением через as
      const panel = node instanceof HTMLDivElement ? node : null

      panelRef.current = panel
      // ref-колбэк потребителя в React 19 вправе вернуть cleanup — его нельзя терять
      if (typeof ref === 'function') return ref(panel)
      if (ref) ref.current = panel
      return undefined
    },
    [ref],
  )

  const updatePosition = useCallback(() => {
    const anchor = anchorRef.current
    const panel = panelRef.current
    if (!anchor || !panel) return

    const next = computePosition(anchor, panel, placement, matchAnchorWidth)
    setPosition((prev) => (samePosition(prev, next) ? prev : next))
  }, [anchorRef, placement, matchAnchorWidth])

  // Первый расчёт до отрисовки: непозиционированный кадр на экран не попадает
  useLayoutEffect(() => {
    if (!isOpen) return
    updatePosition()
  }, [isOpen, updatePosition])

  /*
   * Вторая попытка измерения — для панели, открытой уже на первом рендере (⌘K, восстановление
   * из адреса страницы, стенд). В слое раскладки якоря там ещё нет: React привязывает ref узла
   * позже, чем запускает слои раскладки его детей, а Popover — ребёнок. К моменту пассивных
   * эффектов того же коммита ref уже привязан, и замер удаётся.
   *
   * Круга не возникает: успешное измерение снимает условие, а когда якоря нет вовсе,
   * зависимости не меняются и эффект второй раз не запускается.
   */
  useEffect(() => {
    if (!isOpen || position !== null) return
    updatePosition()
  }, [isOpen, position, updatePosition])

  /*
   * Фокус переносится не раньше первого успешного измерения: до него панель скрыта
   * visibility: hidden, а невидимый узел фокус не принимает — меню, открытое сразу,
   * оставило бы фокус в фоне. Ref-замок держит перенос ровно одним за открытие:
   * пересчёт координат при прокрутке фокус не крадёт.
   */
  const autoFocusedRef = useRef(false)

  useLayoutEffect(() => {
    if (!isOpen) {
      autoFocusedRef.current = false
      return
    }
    if (!autoFocus || autoFocusedRef.current || position === null) return

    const panel = panelRef.current
    if (!panel) return

    autoFocusedRef.current = true
    const [first] = focusablesIn(panel)
    // Фокусировать нечего — фокус берёт сама панель: иначе он остался бы в фоне
    ;(first ?? panel).focus({ preventScroll: true })
  }, [isOpen, autoFocus, position])

  /*
   * Закрытие возвращает фокус на якорь, если он был внутри панели или потерян вместе с ней.
   * Слой раскладки, а не пассивный эффект: чистка обязана успеть до удаления узлов из DOM,
   * иначе document.activeElement уже сброшен и «внутри» не отличить от «пользователь ушёл».
   */
  useLayoutEffect(() => {
    if (!isOpen) return

    /*
     * Узел якоря захватывается на открытии, а не читается из рефа в момент закрытия:
     * потребитель вправе держать якорь на inline-колбэке, и тогда React обнулит его ref
     * в той же фазе коммита, но раньше этой чистки — возвращать фокус было бы уже некуда.
     */
    const anchor = anchorRef.current

    return () => {
      const panel = panelRef.current
      const active = document.activeElement
      const inside = panel !== null && active instanceof Node && panel.contains(active)
      // Панель уже снята из DOM вместе с фокусом — браузер сбросил его в body
      const lost = active === null || active === document.body

      /*
       * Якорь мог исчезнуть, пока панель была открыта: действие из самой панели удаляет
       * строку вместе с её кебабом. focus() на отсоединённом узле молча ничего не делает,
       * и фокус остался бы в body — тогда его принимает ближайший живой контейнер.
       */
      if (!(inside || lost)) return
      // isConnected: focus() на отсоединённом узле молча ничего не делает, и фокус
      // остался бы в body — потребителю это видно, а не «фокус ушёл в никуда».
      if (anchor?.isConnected === true) anchor.focus({ preventScroll: true })
    }
  }, [isOpen, anchorRef])

  // Панель зафиксирована в системе окна и сама за якорем не едет: прокрутка и ресайз — наши
  useEffect(() => {
    if (!isOpen) return

    let frame = 0
    const schedule = () => {
      if (frame !== 0) return
      frame = requestAnimationFrame(() => {
        frame = 0
        updatePosition()
      })
    }

    // Перехват, а не всплытие: событие scroll не всплывает, а прокручиваться может любой предок
    window.addEventListener('scroll', schedule, true)
    window.addEventListener('resize', schedule)

    /*
     * Содержимое приезжает с сервера уже после открытия (подсказки поиска, страница опций):
     * без наблюдателя перевёрнутая вверх панель уползла бы из-под якоря.
     * В jsdom ResizeObserver не реализован — отсутствие API не должно ронять тесты.
     */
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(schedule)
    if (observer) {
      if (panelRef.current) observer.observe(panelRef.current)
      if (anchorRef.current) observer.observe(anchorRef.current)
    }

    return () => {
      if (frame !== 0) cancelAnimationFrame(frame)
      window.removeEventListener('scroll', schedule, true)
      window.removeEventListener('resize', schedule)
      observer?.disconnect()
    }
  }, [isOpen, updatePosition, anchorRef])

  useEffect(() => {
    if (!isOpen) return

    /*
     * Нажатие, а не клик: click приходит только после отпускания кнопки (панель залипает под
     * пальцем) и срабатывает на общем предке, если выделение началось внутри панели.
     * Нажатие по якорю кликом вне не считается — иначе панель закрылась бы здесь и тут же
     * открылась обратно обработчиком самого триггера.
     */
    const handlePointerDown = (event: PointerEvent) => {
      const target = event.target
      if (!(target instanceof Node)) return

      const panel = panelRef.current
      const anchor = anchorRef.current
      if (panel?.contains(target) || anchor?.contains(target)) return

      close()
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      // Содержимое уже обработало клавишу: Combobox по первому Esc чистит запрос, а не закрывается
      if (event.defaultPrevented) return

      const panel = panelRef.current
      if (!panel) return

      const anchor = anchorRef.current
      const active = document.activeElement instanceof Node ? document.activeElement : null

      if (event.key === 'Escape') {
        /*
         * preventDefault, а не stopPropagation: модалка слушает Esc на document и уважает
         * defaultPrevented. Без этого один Esc закрывал бы и поповер, и модалку под ним —
         * когда фокус остался на якоре внутри модальной формы (autoFocus={false}).
         */
        event.preventDefault()
        close()
        return
      }

      if (event.key !== 'Tab') return

      const inPanel = active !== null && panel.contains(active)
      const onAnchor = anchor !== null && active !== null && anchor.contains(active)

      if (onAnchor) {
        // Назад с якоря — уход в фон: панель закрывается, фокус едет штатно
        if (event.shiftKey) {
          close()
          return
        }

        // У якоря могут быть свои контролы (кнопка очистки, подсказка ⌘K) — сперва обходим их
        const anchorFocusables = focusablesIn(anchor)
        const last = anchorFocusables[anchorFocusables.length - 1]
        if (last !== undefined && active !== last) return

        // Портал лежит в конце body: без перехвата Tab прыгнул бы из триггера в подвал страницы
        event.preventDefault()
        const [first] = focusablesIn(panel)
        ;(first ?? panel).focus({ preventScroll: true })
        return
      }

      if (!inPanel) return

      const focusables = focusablesIn(panel)
      const atEdge = (edge: HTMLElement | undefined) => focusables.length === 0 || active === edge
      const leaving = event.shiftKey
        ? atEdge(focusables[0])
        : atEdge(focusables[focusables.length - 1])
      if (!leaving) return

      /*
       * Ушли за край панели. Возврат на якорь вместо «следующего узла документа» — сознательное
       * упрощение: искать его по всему документу дорого, а якорь всегда предсказуем.
       */
      event.preventDefault()
      close()
      anchor?.focus({ preventScroll: true })
    }

    // Всплытие, а не перехват: содержимое вправе обработать Esc раньше и остановить событие
    document.addEventListener('pointerdown', handlePointerDown)
    document.addEventListener('keydown', handleKeyDown)

    return () => {
      document.removeEventListener('pointerdown', handlePointerDown)
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [isOpen, close, anchorRef])

  /** Фокус ушёл мимо панели и якоря: Tab не имеет права оставить панель открытой в фоне. */
  const handleBlur = (event: ReactFocusEvent<HTMLDivElement>) => {
    onBlur?.(event)

    const next = event.relatedTarget
    // Фокус потерян в никуда (нажатие по неинтерактивному месту панели) — это не уход
    if (!(next instanceof Node)) return
    if (panelRef.current?.contains(next) || anchorRef.current?.contains(next)) return

    close()
  }

  if (!isOpen) return null

  const computedStyle: PanelStyle = position
    ? {
        top: position.top,
        left: position.left,
        '--popover-anchor-width': `${position.anchorWidth}px`,
        '--popover-available-h': `${position.available}px`,
      }
    : // Якоря ещё нет: панель измеряется, но на экран не показывается — вторая попытка
      // замера приходит пассивным эффектом того же коммита
      { top: 0, left: 0, visibility: 'hidden' }

  /*
   * Координаты вычисляются здесь и потому идут последними, но остальное из style потребителя
   * сохраняется: {...rest} разложен первым, и его style иначе затирался бы целиком.
   */
  const mergedStyle = { ...rest.style, ...computedStyle }

  return createPortal(
    <Surface
      // Тема экрана, из которого открыта панель (вход — тёмный); явный data-theme потребителя сильнее
      data-theme={portalTheme ?? undefined}
      {...rest}
      ref={attachPanel}
      id={panelId}
      className={cx(s.root, matchAnchorWidth && s.matchAnchorWidth, className)}
      style={mergedStyle}
      variant="overlay"
      radius="menu"
      padding="xs"
      tabIndex={-1}
      data-placement={placement}
      data-side={position?.side ?? (placement.startsWith('top') ? 'top' : 'bottom')}
      onBlur={handleBlur}
    >
      {children}
    </Surface>,
    document.body,
  )
}
